"use client";

import {
  useCallback,
  useEffect,
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
} from "react";
import type { MotionValue } from "framer-motion";
import { getLoopResetLandingIndex } from "./carouselMath";
import { SCROLL_SETTLED_DEBOUNCE_MS } from "./constants";

interface UseCarouselScrollSettlingInput {
  containerRef: RefObject<HTMLDivElement | null>;
  middleCopyEnd: number;
  middleCopyStart: number;
  originalLength: number;
  pitch: number;
  reportSnap: (doubledIndex: number) => void;
  scrollSettledTimerRef: MutableRefObject<number | null>;
  scrollX: MotionValue<number>;
  setCenteredDoubledIndex: Dispatch<SetStateAction<number>>;
}

export function useCarouselScrollSettling({
  containerRef,
  middleCopyEnd,
  middleCopyStart,
  originalLength,
  pitch,
  reportSnap,
  scrollSettledTimerRef,
  scrollX,
  setCenteredDoubledIndex,
}: UseCarouselScrollSettlingInput): void {
  const onScrollSettled = useCallback((): void => {
    const scrollElement = containerRef.current;
    if (!scrollElement || originalLength < 1) return;

    const currentIndex = Math.round(scrollElement.scrollLeft / pitch);
    const landingIndex = getLoopResetLandingIndex({
      index: currentIndex,
      originalLength,
      middleCopyStart,
      middleCopyEnd,
    });

    if (landingIndex !== currentIndex) {
      scrollElement.scrollLeft = landingIndex * pitch;
      scrollX.set(scrollElement.scrollLeft);
    }

    setCenteredDoubledIndex(landingIndex);
    reportSnap(landingIndex);
  }, [
    containerRef,
    middleCopyEnd,
    middleCopyStart,
    originalLength,
    pitch,
    reportSnap,
    scrollX,
    setCenteredDoubledIndex,
  ]);

  useEffect(() => {
    const scrollElement = containerRef.current;
    if (!scrollElement) return;

    const onScroll = (): void => {
      scrollX.set(scrollElement.scrollLeft);

      if (scrollSettledTimerRef.current != null) {
        window.clearTimeout(scrollSettledTimerRef.current);
      }

      scrollSettledTimerRef.current = window.setTimeout(
        onScrollSettled,
        SCROLL_SETTLED_DEBOUNCE_MS,
      );
    };

    scrollElement.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener("scroll", onScroll);
    };
  }, [containerRef, onScrollSettled, scrollSettledTimerRef, scrollX]);
}
